import React from "react";
import { graphql } from "react-relay";
import type { match, NavLinkProps } from "react-router-dom";
import type { Location, Params } from "react-router-dom-v5-compat";

import {
	faCalendar,
	faChartBar,
	faClipboardList,
	faRepeat,
	faUserChart,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import { useLazyLoadQuery } from "~/lib/relayUtil";

import { PrimaryNavLink } from "../shared";

import type { AnalyticsPanelQuery } from "~/__generated__/AnalyticsPanelQuery.graphql";
import type { BillingFeature } from "~/__generated__/utilsDummyQuery.graphql";

import panelStyles from "../shared/NavPanel.module.css";
import panelSectionStyles from "../shared/PanelSection.module.css";

type PaywalledNavLinkProps = Omit<NavLinkProps, "className" | "activeClassName"> & {
	isPaywalled: boolean;
	feature: BillingFeature;
	icon: IconDefinition;
	children: React.ReactNode;
};

function PaywalledNavLink({ to, icon, isPaywalled, feature, children }: PaywalledNavLinkProps) {
	const showPaywall = React.useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			if (isPaywalled) {
				event.preventDefault();
				showFeatureModal(feature);
			}
		},
		[feature, isPaywalled],
	);

	return (
		<PrimaryNavLink to={to} onClick={isPaywalled ? showPaywall : noop}>
			{isPaywalled ? (
				// Need to set the color here to override color from .panelSectionContent class
				<UpgradeIcon style={{ color: "var(--billingColor)" }} fixedWidth />
			) : (
				<FontAwesomeIcon icon={icon} fixedWidth />
			)}
			{children}
		</PrimaryNavLink>
	);
}

const INVALID_ACTIVE_PATHS = ["guidelines", "meetings-report", "recurring-report", "user-report"];

export default function AnalyticsPanel() {
	const { viewer } = useLazyLoadQuery<AnalyticsPanelQuery>(
		graphql`
			query AnalyticsPanelQuery {
				viewer {
					account {
						billingAccount {
							hasAccessToGuidelineAnalytics: hasAccessToFeature(feature: meeting_guidelines_analytics)
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const hasAccessToGuidelineAnalytics = viewer.account.billingAccount.hasAccessToGuidelineAnalytics;

	const isAnalyticsLinkActive = React.useCallback((match: match<Params> | null, location: Location) => {
		if (!match) return false;
		return !INVALID_ACTIVE_PATHS.some(path => location.pathname.includes(path));
	}, []);

	return (
		<div className={panelStyles.panel}>
			<div className={panelStyles.panelHeader}>
				<h1 className={panelStyles.panelTitle}>Meeting analytics</h1>
			</div>
			<div className={panelStyles.main}>
				<div className={cx(panelSectionStyles.panelSection, panelSectionStyles.panelSectionPrimary)}>
					<div className={panelSectionStyles.panelSectionContent}>
						<h2 className={panelSectionStyles.panelSubHeader}>Overview</h2>
						<PrimaryNavLink to="/analytics/" isActive={isAnalyticsLinkActive}>
							<FontAwesomeIcon icon={faChartBar} fixedWidth />
							Dashboard
						</PrimaryNavLink>
						<PaywalledNavLink
							to="/analytics/guidelines"
							icon={faClipboardList}
							isPaywalled={!hasAccessToGuidelineAnalytics}
							feature="meeting_guidelines_analytics"
						>
							Meeting guidelines
						</PaywalledNavLink>
						<h2 className={panelSectionStyles.panelSubHeader}>Drilldowns</h2>
						<PaywalledNavLink
							to="/analytics/meetings-report"
							icon={faCalendar}
							isPaywalled={!hasAccessToGuidelineAnalytics}
							feature="meeting_guidelines_drilldown"
						>
							All meetings
						</PaywalledNavLink>
						<PaywalledNavLink
							to="/analytics/recurring-report"
							icon={faRepeat}
							isPaywalled={!hasAccessToGuidelineAnalytics}
							feature="meeting_guidelines_drilldown"
						>
							Recurring meetings
						</PaywalledNavLink>
						<PaywalledNavLink
							to="/analytics/user-report"
							icon={faUserChart}
							isPaywalled={!hasAccessToGuidelineAnalytics}
							feature="meeting_guidelines_drilldown"
						>
							People&apos;s time in meetings
						</PaywalledNavLink>
					</div>
				</div>
			</div>
		</div>
	);
}
